import type { WritableDeep } from 'type-fest';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import type { Getters } from '../../../components/DocumentEditor/types/props.type';
import type { DocumentEditorCmp_RecursiveSection } from '../../../components/DocumentEditor/fragments/recursive-section.fragment';

import { DocumentTemplatePage_DocumentTemplateVersions_QueryDocument } from '../queries/document-template-versions.query';

import type { DocumentTemplatePage_OneDocumentTemplate } from './useFindOneDocumentTemplateQuery.hook';

export const useGetters =
  (): Getters<DocumentTemplatePage_OneDocumentTemplate> => {
    const {
      documentTemplateFileCategoriesCodeMap,
      fileDirectoryCategoriesCodeMap,
    } = useReferencesContext();

    return {
      editable: ((
        documentTemplate: DocumentTemplatePage_OneDocumentTemplate,
      ) => ({
        code: documentTemplate.code,
        title: documentTemplate.document.title,
        virtualRoot: documentTemplate.document.virtualRoot,
      })) as <W extends boolean = false>(
        editable: W extends false
          ? DocumentTemplatePage_OneDocumentTemplate
          : WritableDeep<DocumentTemplatePage_OneDocumentTemplate>,
      ) => Readonly<{
        virtualRoot: W extends false
          ? DocumentEditorCmp_RecursiveSection
          : WritableDeep<DocumentEditorCmp_RecursiveSection>;
        title: string;
        code: string;
      }>,
      printing: (
        documentTemplate: DocumentTemplatePage_OneDocumentTemplate,
      ) => {
        const {
          printing: {
            pdfPrints,
            pdfPrintsIncrement,
            printSettings: { addFooters, addHeaders, printStyleId },
          },
        } = documentTemplate;

        return {
          __typename: 'DocumentTemplatePrinting' as const,
          printSettings: {
            __typename: 'PrintSettings' as const,
            addFooters,
            addHeaders,
            printStyleId,
          },
          pdfPrints,
          pdfPrintsIncrement,
        };
      },
      storage: (
        documentTemplate: DocumentTemplatePage_OneDocumentTemplate,
      ) => ({
        directoryCategoryId:
          fileDirectoryCategoriesCodeMap.DocumentTemplate._id,
        entityId: documentTemplate._id,
        imageCategory: documentTemplateFileCategoriesCodeMap.TextEditorImages,
        pdfCategory: documentTemplateFileCategoriesCodeMap.PDFPrints,
      }),
      versions: () => ({
        queryDocument:
          DocumentTemplatePage_DocumentTemplateVersions_QueryDocument,
      }),
    };
  };
